/**
 * B7 — per-kind OSNAP candidate generators.
 *
 * Split out of `resolveObjectSnap.ts` to keep that file under the
 * 350-LOC threshold (CLAUDE.md "Контроль размера файлов"). One pure
 * function per renderable kind; each appends `{ mode, world, dist }`
 * tuples onto a caller-supplied accumulator.
 *
 * No DOM / THREE imports — pure 2D math, fully unit-testable.
 */

import { bulgeArc, bulgeArcPointAt } from "@dxf-ai-editor/drawing-model";

import { ellipseMinorUnit, tessellateEllipse } from "../adapters/arcEllipseSpline";
import {
  polylinePlane,
  type PolylinePlane,
} from "../adapters/geom/polylinePlane";
import type { RenderableEntity, Vec2, Vec3 } from "../types";
import { isMetadataStubRenderable } from "../state/metadataStub";
import { osnapCapabilityMap } from "./osnapCapabilities";
import { osnapPolylineSweptRuns } from "./osnapSweptRuns";
import type { OsnapMode } from "./osnapTypes";

/** Working hit emitted by per-kind generators. */
export interface RawHit {
  mode: OsnapMode;
  world: Vec2;
  ownerEntityId: string;
  distWorld: number;
}

/** Distance in world-units between two 2D points. */
export function dist2D(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Lift a `Vec3` to a `Vec2` (drops Z; renderer is 2D in PR-A). */
export function toVec2(v: Vec3): Vec2 {
  return { x: v.x, y: v.y };
}

/** Closest point on the segment `a→b` to the world point `p`. */
export function closestPointOnSegment(a: Vec2, b: Vec2, p: Vec2): Vec2 {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 <= 0) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return { x: a.x + dx * t, y: a.y + dy * t };
}

/** Perpendicular foot of `p` onto the infinite line through `a→b`. */
export function perpendicularFootOnLine(a: Vec2, b: Vec2, p: Vec2): Vec2 | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 <= 0) return null;
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  return { x: a.x + dx * t, y: a.y + dy * t };
}

/**
 * EXTENSION test shared by LINE / POLYLINE (open ends) / SPLINE
 * (control-polygon legs): the perpendicular foot of `cursor` on the
 * INFINITE line through `a→b`, gated to only the requested side.
 *   - `side: "before"` → valid only when cursor projects BEFORE `a`
 *     (t < 0) — the extension beyond `a`, away from `b`.
 *   - `side: "after"`  → valid only when cursor projects AFTER `b`
 *     (t > 1) — the extension beyond `b`, away from `a`.
 * Returns null when the segment is degenerate (a===b) or the cursor
 * projects onto the segment itself / the wrong side.
 */
export function extensionFoot(
  a: Vec2,
  b: Vec2,
  cursor: Vec2,
  side: "before" | "after",
): Vec2 | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 <= 0) return null;
  const t = ((cursor.x - a.x) * dx + (cursor.y - a.y) * dy) / len2;
  if (side === "before" ? t >= 0 : t <= 1) return null;
  return { x: a.x + dx * t, y: a.y + dy * t };
}

/** Closest point on circle (centre `c`, radius `r`) to `p`. */
export function closestPointOnCircle(c: Vec2, r: number, p: Vec2): Vec2 | null {
  const dx = p.x - c.x;
  const dy = p.y - c.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len <= 0) return null;
  return { x: c.x + (dx / len) * r, y: c.y + (dy / len) * r };
}

/**
 * Tangent point on a circle visible from external `p`. AutoCAD picks
 * whichever of the two tangents is closer to the cursor.
 */
export function tangentPointOnCircle(
  c: Vec2,
  r: number,
  p: Vec2,
): Vec2 | null {
  const dx = p.x - c.x;
  const dy = p.y - c.y;
  const d2 = dx * dx + dy * dy;
  if (d2 <= r * r) return null;
  const d = Math.sqrt(d2);
  const cosT = r / d;
  const sinT = Math.sqrt(Math.max(0, 1 - cosT * cosT));
  const ux = dx / d;
  const uy = dy / d;
  const t1: Vec2 = {
    x: c.x + r * (ux * cosT - uy * sinT),
    y: c.y + r * (ux * sinT + uy * cosT),
  };
  const t2: Vec2 = {
    x: c.x + r * (ux * cosT + uy * sinT),
    y: c.y + r * (-ux * sinT + uy * cosT),
  };
  return dist2D(t1, p) <= dist2D(t2, p) ? t1 : t2;
}

/** True if `angle` falls within the arc swept from `start` to `end`. */
export function angleInArc(
  angle: number,
  start: number,
  end: number,
  ccw: boolean,
): boolean {
  const TAU = Math.PI * 2;
  function norm(a: number): number {
    let x = a - start;
    while (x < 0) x += TAU;
    while (x >= TAU) x -= TAU;
    return x;
  }
  let sweep = end - start;
  if (ccw) {
    while (sweep < 0) sweep += TAU;
    if (sweep === 0) sweep = TAU;
  } else {
    while (sweep > 0) sweep -= TAU;
    if (sweep === 0) sweep = -TAU;
    sweep = -sweep;
  }
  return norm(ccw ? angle : 2 * start - angle) <= sweep + 1e-9;
}

/**
 * Normalise an ELLIPSE's start/end sweep to `tessellateEllipse`'s OWN
 * convention: `end - start`, `+2π` if negative, `=== 0` ⇒ full ellipse (a
 * full revolution, not a degenerate zero-sweep). Single source of truth for
 * both `isFullEllipseSweep` below and `handleEllipse`'s downstream
 * `complementSweep` math, so the two can never drift apart.
 */
function normalizeEllipseSweep(start: number, end: number): number {
  let sweep = end - start;
  if (sweep < 0) sweep += Math.PI * 2;
  if (sweep === 0) sweep = Math.PI * 2;
  return sweep;
}

/**
 * True iff an ELLIPSE's `start`..`end` sweep covers a FULL closed ellipse —
 * no open end to extend past (same reasoning as CIRCLE, whose capability
 * map omits `extension` entirely).
 *
 * Single source of truth shared by `handleEllipse`'s EXTENSION emit gate
 * AND the EXTENSION-BROADEN instance-level predicate (`canEmitExtension`
 * below, consumed by `resolveObjectSnap.ts`'s `gatherExtensionCurvedEntities`
 * — @codex round-8 P2: a KIND-level `canSnapKind` check alone let closed/full
 * instances silently consume the broadened scan/slot budget even though they
 * never actually emit `extension`).
 */
export function isFullEllipseSweep(start: number, end: number): boolean {
  const FULL_SWEEP_EPS = 1e-9;
  return normalizeEllipseSweep(start, end) >= Math.PI * 2 - FULL_SWEEP_EPS;
}

/**
 * Instance-level predicate: true iff this SPECIFIC entity (not just its
 * KIND) can actually emit an `extension` candidate. `osnapCapabilityMap` /
 * `canSnapKind` are KIND-level ("ellipse" and "polyline" CAN carry
 * extension) — but a CLOSED polyline or a FULL (closed) ellipse instance
 * never does (mirrors the `!g.closed` gate in `handlePolyline` and the
 * `isFullEllipseSweep` gate in `handleEllipse` exactly, so this can never
 * silently drift out of sync with the real emit logic). A polyline carrying a
 * group-39 THICKNESS is the one exception on the closed side, and it is the
 * same mirror rather than a loosening: the sweep gives it RULINGS, which are
 * open 2-point runs, so `handlePolyline` really does emit `extension` for it
 * (@codex PR #785 round 16).
 *
 * @codex round-8 P2 — `gatherExtensionCurvedEntities`'s broadened scan/slot
 * budget was previously spent on ANY kind-eligible id regardless of whether
 * the specific instance could ever emit `extension`, so a dense run of
 * closed polylines / full ellipses ahead of a genuine open/partial entity
 * in spatial-index order could exhaust the cap before the real candidate
 * was ever reached. LINE / ARC fall through to `true` unconditionally (a
 * DXF ARC always has a distinct start/end; the round-11 P2 per-cursor
 * PRODUCTIVITY probe in `gatherExtensionCurvedEntities` — see that doc —
 * separately catches a degenerate zero-length LINE, which this KIND/
 * INSTANCE-level check cannot see).
 *
 * SPLINE also falls through to `true` — NOT because it has no closed
 * variant (DXF SPLINE has `closed`/`periodic`, `packages/drawing-model`'s
 * `SplineEntity` carries both), but because that state is dropped before
 * it ever reaches `RenderableEntity["geometry"]` (`kind: "spline"` has no
 * `closed`/`periodic` field — @codex round-13 P2, GitHub review; see the
 * SPLINE-CLOSED-EXTENSION-SUPPRESS entry in docs/test-gaps/sub-osnap.md
 * and the matching comment in `handleSpline`'s EXTENSION block below for
 * why suppressing it here alone, without the missing data + a renderer
 * fix, would be worse than today's consistent-but-imperfect behaviour).
 */
export function canEmitExtension(entity: RenderableEntity): boolean {
  const g = entity.geometry;
  switch (g.kind) {
    case "polyline":
      // A CLOSED run has no free end — EXCEPT that a group-39 THICKNESS gives it
      // RULINGS, and a ruling is an OPEN 2-point edge of the swept surface whose
      // ends DO extend (@codex PR #785 round 16). Keeping the old `!g.closed`
      // answer here would let `gatherExtensionCurvedEntities` skip a closed
      // extruded polyline that `handlePolyline` genuinely emits `extension` for,
      // which is the drift this predicate's own doc warns about.
      return (g.thicknessSweep !== undefined || !g.closed) && g.points.length >= 2;
    case "ellipse":
      return !isFullEllipseSweep(g.start, g.end);
    default:
      return true;
  }
}

/**
 * Helper used by per-kind generators: emit `(mode, world)` only when the
 * mode is in BOTH the active OSMODE set AND the entity kind's capability
 * set, and the point lies within `apertureWorld` of `cursor`.
 *
 * `capable` makes `osnapCapabilityMap` LOAD-BEARING: a handler can no
 * longer emit a mode the map does not list for its kind. Because the map
 * equals today's per-kind emits byte-exact, the capability gate is a
 * runtime no-op today — but it makes the map (not the handler bodies) the
 * authoritative contract for "which kind emits which mode".
 */
function makeEmit(
  out: RawHit[],
  ownerId: string,
  cursor: Vec2,
  modes: ReadonlySet<OsnapMode>,
  apertureWorld: number,
  capable: readonly OsnapMode[],
): (mode: OsnapMode, world: Vec2) => void {
  return (mode, world) => {
    if (!modes.has(mode)) return;
    if (!capable.includes(mode)) return;
    const d = dist2D(world, cursor);
    if (d > apertureWorld) return;
    out.push({ mode, world, ownerEntityId: ownerId, distWorld: d });
  };
}

/**
 * Append every snap-point candidate for one renderable. Dispatches on
 * the geometry kind; compound kinds (hatch / dimension / multileader)
 * produce no candidates because the spatial index already surfaces
 * their child primitives separately.
 */
export function appendCandidatesForEntity(
  entity: RenderableEntity,
  cursor: Vec2,
  modes: ReadonlySet<OsnapMode>,
  apertureWorld: number,
  out: RawHit[],
  // @codex #336 — test seam: the capability set is injectable so the
  // consistency test can collect a handler's RAW (ungated) emissions and
  // catch a map that is too NARROW (the production gate would otherwise
  // suppress the extra emit, letting `emitted === map` pass with a missing
  // mode). Defaults to the real map — production behaviour unchanged.
  capabilityOverride?: readonly OsnapMode[],
): void {
  const g = entity.geometry;
  const capable = capabilityOverride ?? osnapCapabilityMap[g.kind];
  // Single source of truth for "this kind snaps to nothing" — covers the
  // compound kinds (hatch / wipeout / viewport / array-rect / dimension /
  // multileader) whose child primitives the spatial index surfaces
  // separately. Driven by the map, so adding a no-snap kind needs only a
  // `[]` map entry, no new `case`.
  if (capable.length === 0) return;
  const emit = makeEmit(out, entity.id, cursor, modes, apertureWorld, capable);
  switch (g.kind) {
    case "line":
      handleLine(g, cursor, emit);
      return;
    case "polyline":
      handlePolyline(g, cursor, emit);
      return;
    case "mesh-wire":
      // A heavy POLYLINE mesh: every wire run snaps by the SAME rule a
      // polyline does (`emitPolylineRunCandidates` — one truth, not a second
      // copy of the segment math), and the mesh itself contributes NO
      // insertion point (@codex PR #785 round 3, F4 — see the
      // `osnapCapabilityMap` entry).
      for (const run of g.runs) {
        emitPolylineRunCandidates(run.points, run.closed, undefined, cursor, emit);
      }
      return;
    case "circle":
      handleCircle(g, cursor, emit);
      return;
    case "arc":
      handleArc(g, cursor, emit);
      return;
    case "ellipse":
      handleEllipse(g, cursor, emit, apertureWorld);
      return;
    case "spline":
      handleSpline(g, cursor, emit);
      return;
    case "point":
      // CACHE-DIRECT METADATA STUB — `kind: "point"` is ALSO the shape
      // `state/spatialBuilder.ts metadataToSpatialStub` gives an entity of ANY
      // type that the renderer never lifted (sub-pixel at the LOD pre-classify;
      // `createMetadataStubResolver` answers for it so the id keeps flowing
      // through the visible set). Its `position` is the bbox CENTRE, not
      // geometry: emitting NODe there would put a snap marker where NOTHING is
      // drawn and — for a `Block` stub — at the centre of the definition's bbox
      // rather than the INSERT's insertion point. AutoCAD's NODe snaps to a
      // POINT object / dimension definition point only, and never marks a spot
      // with no object drawn, so a stub contributes NOTHING. A genuine DXF
      // POINT (`type === "Point"`) is unaffected: its `position` IS the node.
      // (The real geometry becomes snappable the moment `FrameLoops` Pass 2
      // lifts the id and publishes the real renderable into `renderableById`.)
      if (isMetadataStubRenderable(entity)) return;
      emit("node", toVec2(g.position));
      return;
    case "block-insert":
      emit("insertion", toVec2(g.insertion));
      return;
    case "text":
    case "mtext":
      emit("insertion", toVec2(g.position));
      return;
    default:
      // Compound kinds (hatch / wipeout / viewport / array-rect /
      // dimension / multileader) — their `osnapCapabilityMap` entry is
      // `[]`, so the early `capable.length === 0` return above already
      // handled them. This default keeps the switch exhaustive over the
      // kind union without re-listing each no-snap kind.
      return;
  }
}

type Emit = (mode: OsnapMode, world: Vec2) => void;

function handleLine(
  g: Extract<RenderableEntity["geometry"], { kind: "line" }>,
  cursor: Vec2,
  emit: Emit,
): void {
  const a = toVec2(g.a);
  const b = toVec2(g.b);
  emit("endpoint", a);
  emit("endpoint", b);
  emit("midpoint", { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const foot = perpendicularFootOnLine(a, b, cursor);
  if (foot) emit("perpendicular", foot);
  const near = closestPointOnSegment(a, b, cursor);
  emit("nearest", near);
  const extBefore = extensionFoot(a, b, cursor, "before");
  if (extBefore) emit("extension", extBefore);
  const extAfter = extensionFoot(a, b, cursor, "after");
  if (extAfter) emit("extension", extAfter);
  // OSNAP-SNAP-EXPAND — PARALLEL (bit 8192) stays the documented degenerate
  // placeholder (`OSNAP_MODES_PENDING_IMPLEMENTATION` in resolveObjectSnap.ts
  // — no reference-direction gate yet, behaves like Nearest until the
  // in-progress draft-vector arm lands). This slice only widens WHICH kinds
  // emit it (see `handlePolyline`), not this semantic gap.
  emit("parallel", near);
}

/**
 * The polyline's candidates — on the BASE run and, when the entity declares a
 * DXF group-39 THICKNESS, on the swept surface that base generates.
 *
 * # The defect this closes (@codex PR #785 round 16, the OSNAP fan-out)
 *
 * This function read `g.points` and nothing else, so after round 15 gave the
 * canvas the thickness sweep (`adapters/geom/polylineThicknessSweep.ts`) the
 * painted TOP outline and the RULINGS carried no snap point at all: on the
 * round-16 witness (extrusion `(1,1,1)`, thickness 5 on `(0,0) → (10,0)`) the
 * nearest candidate to the painted top vertex `(2.886751345948129,
 * 2.886751345948129)` was the base endpoint at the ORIGIN, 4.08248290463863
 * away, against a running-snap aperture of 0.08 world units at an ordinary
 * zoom. `raycast/distance.ts` and `raycast/windowSelect.ts` closed the same gap
 * for the pick and the window select in this round; the snaps are the third
 * consumer of the one shape, and `snap/osnapSweptRuns.ts` is where they read it
 * (it carries the AutoCAD argument for sweeping the CENTRELINE, plus the one
 * declared residual).
 *
 * Each run goes through {@link emitPolylineRunCandidates} unchanged — the base
 * and its translated copy WITH the authored bulges (a translation does not
 * change a span's bow, so the top outline is the same arc moved, evaluated in
 * the same plane), a ruling WITHOUT them (it is a straight edge whatever the
 * base declares). A thickness-less polyline yields exactly one run and the call
 * below is byte-identical to the one it replaced.
 */
function handlePolyline(
  g: Extract<RenderableEntity["geometry"], { kind: "polyline" }>,
  cursor: Vec2,
  emit: Emit,
): void {
  for (const run of osnapPolylineSweptRuns(g, g.points)) {
    emitPolylineRunCandidates(
      run.points,
      run.closed,
      run.ruling ? undefined : g.bulges,
      cursor,
      emit,
      g.extrusion,
    );
  }
}

/**
 * Samples used to search a TILTED bulged span for its NEArest / PERpendicular
 * point. The same count `handleEllipse` searches a tilted ellipse rim at, and
 * for the same reason: in the top view the span's circular arc PROJECTS to an
 * elliptical one, which has no closed-form nearest point.
 */
const TILTED_ARC_SNAP_SAMPLES = 32;

/**
 * The MIDpoint / NEArest / PERpendicular / EXTENSION candidates of a BULGED
 * span whose arc is NOT in the WCS XY plane, returning `false` when the span
 * is degenerate (no arc) so the caller falls through to its straight handling.
 *
 * # Why this branch has to exist
 *
 * A bulge (DXF group 42) is a 2D-polyline field measured in the entity's own
 * OCS plane, and `heavyPolylineWcsResolver` lifted the vertices out of that
 * plane before the renderer ever saw them. `bulgeArc` on the LIFTED points
 * builds a circle in the WCS XY plane instead — for extrusion (0,1,0) that put
 * a semicircle's 5-unit bow on world Y, where the drawing (and
 * `computeEntityWcsBounds`) puts it on world Z. The renderer now evaluates the
 * arc in the plane (`adapters/geom/polylineWidthBand.ts::
 * polylineCentrelineSamples`); snapping the old flat circle would leave every
 * MIDpoint / NEArest marker on a curve that is no longer drawn — the
 * snap-doesn't-match-render bug the chord convention was protecting against in
 * the other direction (@codex round-3 #457).
 *
 * # What is exact and what is sampled
 *
 * MIDpoint is EXACT: `bulgeArcPointAt(arc, 0.5)` in the plane's own
 * coordinates, lifted. AutoCAD's MIDpoint of an arc span is the arc's
 * midpoint, and that is a point, not a search.
 *
 * NEArest / PERpendicular are SEARCHED, because the top view projects the
 * in-plane circle to an ELLIPSE and the cursor distance is measured in that
 * projection. This is exactly what `handleEllipse` already does for a tilted
 * rim, at the same sample count. The shortest distance from a point to a
 * smooth curve meets it perpendicularly, so an INTERIOR best sample is both
 * the nearest point and the perpendicular foot — which is why the flat branch
 * below emits one point for both modes too. When the best sample is an END of
 * the span the cursor is off the swept range: there is no perpendicular foot
 * (same gate as the flat branch), the nearest DRAWN point is that end, and a
 * free run end additionally offers the arc's curved EXTENSION — the nearest
 * point of the COMPLEMENT sweep, the tilted analogue of the flat branch's
 * off-sweep `onCircle`.
 *
 * `bulgeArc` / `bulgeArcPointAt` stay `drawing-model`'s one sign-sensitive
 * truth; only the FRAME they are evaluated in changes here.
 */
function emitTiltedArcSpanCandidates(
  plane: PolylinePlane,
  aRaw: Vec3,
  bRaw: Vec3,
  bulge: number,
  cursor: Vec2,
  emit: Emit,
  atFreeEnd: boolean,
): boolean {
  const la = plane.toLocal(aRaw);
  const lb = plane.toLocal(bRaw);
  const arc = bulgeArc(la, lb, bulge);
  if (!arc) return false;

  // An OCS-planar run has ONE elevation, so `la.z === lb.z`; the interpolation
  // matches `polylineSpanSubSegments`' rule verbatim for the block-child case
  // where the two ends were lifted to different Z.
  const lift = (x: number, y: number, u: number): Vec2 => {
    const w = plane.toWorld({ x, y, z: la.z + (lb.z - la.z) * u });
    return { x: w.x, y: w.y };
  };

  const mid = bulgeArcPointAt(arc, 0.5);
  emit("midpoint", lift(mid.x, mid.y, 0.5));

  let best: Vec2 | null = null;
  let bestDist = Infinity;
  let bestInterior = false;
  for (let i = 0; i <= TILTED_ARC_SNAP_SAMPLES; i += 1) {
    const u = i / TILTED_ARC_SNAP_SAMPLES;
    const q = bulgeArcPointAt(arc, u);
    const w = lift(q.x, q.y, u);
    const d = dist2D(w, cursor);
    if (d < bestDist) {
      bestDist = d;
      best = w;
      bestInterior = i > 0 && i < TILTED_ARC_SNAP_SAMPLES;
    }
  }
  if (!best) return true;

  emit("nearest", best);
  if (bestInterior) {
    emit("perpendicular", best);
    return true;
  }
  if (!atFreeEnd) return true;

  // The curved EXTENSION: the rest of the same circle, walked in the plane and
  // projected, nearest point to the cursor.
  const sign = arc.signedSweep >= 0 ? 1 : -1;
  const complement = sign * (Math.PI * 2 - Math.abs(arc.signedSweep));
  if (!(Math.abs(complement) > 0) || !Number.isFinite(complement)) return true;
  const from = arc.startAngle + arc.signedSweep;
  let ext: Vec2 | null = null;
  let extDist = Infinity;
  for (let i = 0; i <= TILTED_ARC_SNAP_SAMPLES; i += 1) {
    const th = from + complement * (i / TILTED_ARC_SNAP_SAMPLES);
    const w = lift(
      arc.cx + arc.radius * Math.cos(th),
      arc.cy + arc.radius * Math.sin(th),
      1,
    );
    const d = dist2D(w, cursor);
    if (d < extDist) {
      extDist = d;
      ext = w;
    }
  }
  if (ext) emit("extension", ext);
  return true;
}

/**
 * The OSNAP candidates of ONE polyline run — shared by `handlePolyline` and the
 * `mesh-wire` arm (a mesh's rows / columns / face edges ARE polyline runs), so
 * the two can never drift apart.
 *
 * # Bulged spans snap on the ARC (@codex PR #785 round 3, F1)
 *
 * A non-zero group-42 bulge means the span to the next vertex IS a circular
 * arc, and `batchedLayer.ts` now DRAWS it as one. So the MIDpoint of such a
 * span is the arc's midpoint, and NEArest / PERpendicular land on the arc —
 * not on the chord. When the entity is OCS-planar in a TILTED plane
 * (`extrusion`) that arc is not in the WCS XY plane the lifted points project
 * onto, and the candidates follow it into the plane exactly as the renderer
 * does — {@link emitTiltedArcSpanCandidates}. Snapping the chord while the canvas draws
 * the arc is the snap-doesn't-match-render bug the chord convention was
 * protecting against in the other direction (@codex round-3 #457); the two
 * halves land together here, exactly as `docs/test-gaps/sub-osnap.md`'s
 * OSNAP-INTERSECT-EMIT-S2 requires.
 *
 * `bulgeArc` / `bulgeArcPointAt` (`drawing-model/bulgeArc.ts`) are the repo's
 * one truth for the sign-sensitive side-of-chord convention — never re-derived
 * here. PARALLEL is emitted only for STRAIGHT spans: Autodesk's Object Snap Tab
 * reference constrains it to a LINEAR reference object, which is also why
 * `arc` / `ellipse` / `spline` carry no `parallel` capability.
 */
function emitPolylineRunCandidates(
  pts: readonly Vec3[],
  closed: boolean,
  bulges: number[] | undefined,
  cursor: Vec2,
  emit: Emit,
  extrusion?: Vec3,
): void {
  if (pts.length < 1) return;
  // The arc a bulge encodes lives in the entity's OWN OCS plane, and the
  // renderer now evaluates it there too — see `emitTiltedArcSpanCandidates`.
  // `null` for every ordinary (default-extrusion) polyline, which then runs the
  // byte-identical flat code below.
  const plane = polylinePlane(extrusion);
  if (!closed) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (first) emit("endpoint", toVec2(first));
    if (last) emit("endpoint", toVec2(last));
  }
  const n = pts.length;
  const segCount = closed ? n : n - 1;
  for (let i = 0; i < segCount; i++) {
    const aRaw = pts[i];
    const bRaw = pts[(i + 1) % n];
    if (!aRaw || !bRaw) continue;
    const a = toVec2(aRaw);
    const b = toVec2(bRaw);
    const bulge = bulges?.[i] ?? 0;
    const atRunStart = !closed && i === 0;
    const atRunEnd = !closed && i === segCount - 1;
    if (
      plane &&
      bulge !== 0 &&
      emitTiltedArcSpanCandidates(
        plane,
        aRaw,
        bRaw,
        bulge,
        cursor,
        emit,
        atRunStart || atRunEnd,
      )
    ) {
      continue;
    }
    const arc = bulgeArc(a, b, bulge);
    if (arc) {
      emit("midpoint", bulgeArcPointAt(arc, 0.5));
      const centre: Vec2 = { x: arc.cx, y: arc.cy };
      const onCircle = closestPointOnCircle(centre, arc.radius, cursor);
      if (onCircle) {
        const ang = Math.atan2(onCircle.y - centre.y, onCircle.x - centre.x);
        const endAngle = arc.startAngle + arc.signedSweep;
        if (angleInArc(ang, arc.startAngle, endAngle, arc.signedSweep > 0)) {
          emit("nearest", onCircle);
          emit("perpendicular", onCircle);
        } else {
          // Off the swept range: the nearer of the two span ends is the
          // closest DRAWN point (mirrors `handleArc`'s own fallback), and the
          // off-sweep circle point is the arc's curved EXTENSION — offered only
          // at a run's free end, exactly like the straight case below.
          emit("nearest", dist2D(a, cursor) <= dist2D(b, cursor) ? a : b);
          if (atRunStart || atRunEnd) emit("extension", onCircle);
        }
      }
      continue;
    }
    emit("midpoint", { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    const near = closestPointOnSegment(a, b, cursor);
    emit("nearest", near);
    // OSNAP-SNAP-EXPAND — PARALLEL (bit 8192) per straight segment. A
    // polyline segment IS a valid linear PARALLEL reference object per
    // Autodesk's Object Snap Tab reference ("line segment, polyline segment,
    // ray, or xline"), unlike ARC/ELLIPSE/SPLINE (curves — intentionally
    // excluded, see `osnapCapabilityMap`). Same documented degenerate
    // placeholder as LINE's `parallel` (no reference-direction gate yet).
    emit("parallel", near);
    const foot = perpendicularFootOnLine(a, b, cursor);
    if (foot) emit("perpendicular", foot);
    // OSNAP-SNAP-EXPAND — EXTENSION (bit 4096) at the run's OPEN ends only (a
    // CLOSED run has no free endpoint to extend past — mirrors the `endpoint`
    // emit gate above). An interior vertex is a joint between two segments and
    // never extends.
    if (atRunStart) {
      const ext = extensionFoot(a, b, cursor, "before");
      if (ext) emit("extension", ext);
    }
    if (atRunEnd) {
      const ext = extensionFoot(a, b, cursor, "after");
      if (ext) emit("extension", ext);
    }
  }
  let cx = 0;
  let cy = 0;
  let count = 0;
  for (const p of pts) {
    if (!p) continue;
    cx += p.x;
    cy += p.y;
    count += 1;
  }
  if (count > 0) emit("geomcenter", { x: cx / count, y: cy / count });
}

function handleCircle(
  g: Extract<RenderableEntity["geometry"], { kind: "circle" }>,
  cursor: Vec2,
  emit: Emit,
): void {
  const c = toVec2(g.center);
  emit("center", c);
  emit("geomcenter", c);
  const r = g.radius;
  emit("quadrant", { x: c.x + r, y: c.y });
  emit("quadrant", { x: c.x - r, y: c.y });
  emit("quadrant", { x: c.x, y: c.y + r });
  emit("quadrant", { x: c.x, y: c.y - r });
  const near = closestPointOnCircle(c, r, cursor);
  if (near) {
    emit("nearest", near);
    emit("perpendicular", near);
  }
  const tangent = tangentPointOnCircle(c, r, cursor);
  if (tangent) emit("tangent", tangent);
}

function handleArc(
  g: Extract<RenderableEntity["geometry"], { kind: "arc" }>,
  cursor: Vec2,
  emit: Emit,
): void {
  const c = toVec2(g.center);
  emit("center", c);
  emit("geomcenter", c);
  const r = g.radius;
  const startPt: Vec2 = {
    x: c.x + r * Math.cos(g.start),
    y: c.y + r * Math.sin(g.start),
  };
  const endPt: Vec2 = {
    x: c.x + r * Math.cos(g.end),
    y: c.y + r * Math.sin(g.end),
  };
  emit("endpoint", startPt);
  emit("endpoint", endPt);
  let sweep = g.end - g.start;
  if (g.ccw) {
    while (sweep < 0) sweep += Math.PI * 2;
  } else {
    while (sweep > 0) sweep -= Math.PI * 2;
  }
  const midAngle = g.start + sweep / 2;
  emit("midpoint", {
    x: c.x + r * Math.cos(midAngle),
    y: c.y + r * Math.sin(midAngle),
  });
  for (const qa of [0, Math.PI / 2, Math.PI, -Math.PI / 2]) {
    if (angleInArc(qa, g.start, g.end, g.ccw)) {
      emit("quadrant", {
        x: c.x + r * Math.cos(qa),
        y: c.y + r * Math.sin(qa),
      });
    }
  }
  const onCircle = closestPointOnCircle(c, r, cursor);
  if (onCircle) {
    const ang = Math.atan2(onCircle.y - c.y, onCircle.x - c.x);
    if (angleInArc(ang, g.start, g.end, g.ccw)) {
      emit("nearest", onCircle);
      emit("perpendicular", onCircle);
      emit("tangent", onCircle);
    } else {
      // OSNAP-SNAP-EXPAND — EXTENSION (bit 4096): the closest point on the
      // FULL circle falls OUTSIDE the swept angle, i.e. on the arc's curved
      // continuation past one of its endpoints. AutoCAD documents a
      // "temporary extension line OR ARC" for arcs (Object Snap Tab
      // reference) — the arc keeps curving along the same circle, unlike a
      // LINE's straight extension. Mirrors LINE's off-segment gate: any
      // off-sweep point on the circle is a candidate (both directions).
      emit("extension", onCircle);
      const ds = dist2D(startPt, cursor);
      const de = dist2D(endPt, cursor);
      emit("nearest", ds <= de ? startPt : endPt);
    }
  }
}

function handleEllipse(
  g: Extract<RenderableEntity["geometry"], { kind: "ellipse" }>,
  cursor: Vec2,
  emit: Emit,
  apertureWorld: number,
): void {
  const c = toVec2(g.center);
  emit("center", c);
  emit("geomcenter", c);
  const major = toVec2(g.majorAxis);
  const ratio = g.ratio;
  // SL-15 round-8 @codex F4 — the minor QUADRANT rides the TRUE 3D minor axis
  // (`extrusion × major`, ezdxf `minor_axis`) projected to XY, NOT a flat XY
  // rot90. For a planar ellipse this is byte-identical to the old
  // `(−major.y·ratio, major.x·ratio)`; for a tilted ellipse the quadrant now
  // lands where the rim actually renders.
  const majorMag3D = Math.hypot(g.majorAxis.x, g.majorAxis.y, g.majorAxis.z);
  const minorUnit = ellipseMinorUnit(g.majorAxis, g.extrusion);
  const minorX = minorUnit.x * majorMag3D * ratio;
  const minorY = minorUnit.y * majorMag3D * ratio;
  emit("quadrant", { x: c.x + major.x, y: c.y + major.y });
  emit("quadrant", { x: c.x - major.x, y: c.y - major.y });
  emit("quadrant", { x: c.x + minorX, y: c.y + minorY });
  emit("quadrant", { x: c.x - minorX, y: c.y - minorY });
  if (majorMag3D <= 0) return;
  // NEAREST rides the ONE-TRUTH 3D render sampler (`tessellateEllipse`, the same
  // call the renderer + pick make) so the snap point tracks the tilted rim.
  const samples = tessellateEllipse(
    g.center,
    g.majorAxis,
    ratio,
    g.start,
    g.end,
    32,
    g.extrusion,
  );
  let bestPoint: Vec2 | null = null;
  let bestDist = Infinity;
  for (const s of samples) {
    const d = dist2D(s, cursor);
    if (d < bestDist) {
      bestDist = d;
      bestPoint = { x: s.x, y: s.y };
    }
  }
  if (bestPoint) emit("nearest", bestPoint);
  // OSNAP-SNAP-EXPAND — EXTENSION (bit 4096) for a genuine ELLIPTICAL ARC
  // (start/end sweep < full circle). AutoCAD's Object Snap Tab reference
  // documents a temporary extension for elliptical arcs alongside arcs — a
  // FULL (closed) ellipse has no open end to extend past (same reasoning as
  // CIRCLE, whose capability map also omits `extension`). `isFullEllipseSweep`
  // is the one-truth gate (shared with `canEmitExtension`'s EXTENSION-BROADEN
  // instance predicate) so "full" here agrees with what the renderer /
  // `nearest` sampler above already treats as full. `majorMag3D > 0` is
  // already guaranteed at this point (the early return above the NEAREST
  // block covers the degenerate case).
  const sweep = normalizeEllipseSweep(g.start, g.end);
  if (!isFullEllipseSweep(g.start, g.end)) {
    // Tessellate the COMPLEMENT arc (the invisible portion, end → end +
    // complementSweep) — the SAME one-truth sampler the visible-arc
    // `nearest` above uses, just over the OTHER span, so the extension
    // point tracks the true tilted rim exactly like `nearest` does.
    //
    // @codex round-1 P2 — the endParam MUST be `g.end + complementSweep`,
    // NOT `g.start + Math.PI * 2`. For a WRAPPED arc (raw `g.end < g.start`,
    // e.g. start=300°, end=30°, sweeping forward through 0°) the two are
    // only numerically equal when `g.start === 0`; in general
    // `g.start + 2π` overshoots by the FULL visible `sweep` (since
    // `g.start + 2π === g.end + sweep + complementSweep`, i.e. it re-adds
    // the visible arc on top of the complement), so `tessellateEllipse`'s
    // own `endParam − startParam` exceeds a full revolution and re-samples
    // the VISIBLE arc too — a cursor legitimately ON the visible geometry
    // could then spuriously win an `extension` hit. `end + complementSweep`
    // is exactly one revolution regardless of wrap (complementSweep is
    // always in (0, 2π) whenever `sweep` is), so it can never re-include the
    // visible span.
    const complementSweep = Math.PI * 2 - sweep;
    // @codex round-13 P2 — a FIXED 32-segment tessellation under-resolves a
    // large ellipse at a small aperture: consecutive sample chord length
    // grows with `majorMag3D * complementSweep`, so a cursor legitimately
    // ON the invisible continuation, well within `apertureWorld`, can still
    // land strictly BETWEEN two coarse samples and lose the final `emit()`
    // distance gate (reviewer's example: a 100-unit half ellipse with
    // `apertureWorld=1` misses most of the complement except near sample
    // vertices). Scale segment count so the chord length never exceeds
    // `apertureWorld` — `majorMag3D` is a safe (>= true local radius)
    // over-estimate for the arc-length/chord approximation since DXF
    // convention guarantees `majorAxis` is the LONGER axis (ratio <= 1).
    // Clamped to [32, 512]: 32 preserves today's resolution for a small/
    // normal-aperture ellipse (no perf regression), 512 bounds the worst
    // case (a huge ellipse at a microscopic aperture) to a still-cheap
    // per-hover cost.
    //
    // RESIDUAL (documented, not silent — @codex round-15 P2): the 512 upper
    // clamp means the "chord length never exceeds apertureWorld" guarantee
    // above only holds up to that clamp — for `majorMag3D * complementSweep
    // > 512 * apertureWorld` the ACTUAL chord length exceeds apertureWorld
    // again and a cursor genuinely on the invisible continuation, strictly
    // between two (now coarser-than-aperture) samples, can miss the final
    // `emit()` distance gate exactly like the pre-round-13 fixed-32-sample
    // case did. Reviewer's example: a radius-1000 half ellipse at
    // `apertureWorld = 1` needs `1000 * PI ≈ 3142` segments for the
    // guarantee to hold but is clamped to 512 (chord ≈ 6.1, six times the
    // aperture). Raising the clamp only moves this boundary, not removes it
    // (an even larger ellipse / tighter aperture reconstructs the same gap)
    // — the actual fix is an analytic or Newton-refined closest point on the
    // ellipse arc (O(1) cost independent of size/aperture, no chord-length
    // ceiling at all), tracked as **OSNAP-EXTENSION-ELLIPSE-CAP-S1**
    // (`docs/test-gaps/sub-osnap.md`), out of scope for this finding. Pinned
    // by `resolveObjectSnap.test.ts` ("misses an extension point on an
    // ellipse whose required resolution exceeds the 512-segment clamp").
    const apertureFloor = Math.max(apertureWorld, 1e-6);
    const adaptiveSegments = Math.ceil(
      (majorMag3D * complementSweep) / apertureFloor,
    );
    const extSegments = Math.min(512, Math.max(32, adaptiveSegments));
    const extSamples = tessellateEllipse(
      g.center,
      g.majorAxis,
      ratio,
      g.end,
      g.end + complementSweep,
      extSegments,
      g.extrusion,
    );
    // @codex round-10 P2 — `tessellateEllipse`'s own sampling convention
    // (`arcEllipseSpline.ts`, `for (i = 0; i <= segments; i++)`) puts param
    // `startParam` (here `g.end`) at index 0 and `endParam` (here
    // `g.end + complementSweep`, which wraps to `g.start`) at the LAST
    // index — i.e. `extSamples[0]` and `extSamples[extSamples.length - 1]`
    // are EXACTLY the visible arc's own endpoints (the complement and the
    // visible span share their boundary, by construction). Searching the
    // FULL sample set for "nearest" let a cursor hovering ON the visible
    // arc's endpoint itself win an `extension` hit under EXTENSION-only
    // mode — wrong: that point is ON the entity, not beyond it (LINE's
    // `extensionFoot` never returns an on-segment point for the same
    // reason). Slicing off both boundary samples restricts the search to
    // the STRICTLY-invisible interior of the complement.
    //
    // @codex round-13 P2 — with the FIXED 32-sample tessellation, slicing
    // off exactly 2 samples created a dead zone roughly `complementSweep *
    // majorMag3D / 32` wide near each endpoint, wider than a tight aperture
    // could reach (reviewer's example: hovering at (10,-0.1) on a 10x5 half
    // ellipse with a 0.2-world aperture — genuinely on the invisible
    // continuation — found its nearest surviving sample ~0.39 world units
    // away and lost the gate). The adaptive segment count above shrinks
    // that dead zone to well under `apertureWorld` by construction (chord
    // length <= apertureWorld), so a cursor just past the boundary now
    // always has an interior sample within reach.
    // @codex round-17 P2 — classify which SIDE of the visible/invisible
    // boundary the cursor itself is on ANALYTICALLY (no sample-resolution
    // dependency), mirroring exactly how ARC does it (`closestPointOnCircle`
    // + `angleInArc`) instead of a nearest-SAMPLE distance heuristic.
    //
    // @codex round-16 P2 tried a distance-based guard ("is the cursor closer
    // to a shared boundary point than to the best surviving interior
    // sample") to stop `extension` firing for a cursor hovering exactly ON a
    // visible endpoint. @codex round-17 P2 (GitHub review) found that
    // heuristic creates a NEW dead zone: for a cursor genuinely on the
    // invisible side but close to the boundary (10x5 half ellipse,
    // apertureWorld=1, cursor at parameter `2π−0.01`), the boundary point
    // (~0.05 world units away) can be CLOSER than the coarse nearest
    // interior sample (~0.44 away) even though the cursor's OWN true
    // position is exactly on the curve (distance 0) — the guard then
    // wrongly suppressed a legitimate, well-within-aperture extension.
    //
    // Root fix: project `cursor` into the ellipse's own parametric basis and
    // reuse `angleInArc` (the SAME visible-sweep test ARC uses,
    // boundary-inclusive) to decide whether that angle is VISIBLE (suppress
    // — an on-object cursor, exactly mirroring LINE/ARC) or INVISIBLE
    // (proceed to the interior-sample search below, unchanged from
    // round-13).
    //
    // @codex round-18 P2 (GitHub review) — for a TILTED ellipse (extrusion
    // not the default (0,0,1), or `majorAxis.z != 0`), the 3D-perpendicular
    // major/minor axes do NOT stay perpendicular after this module's
    // orthographic projection to the XY plane (documented 2D approximation
    // — "renderer is 2D in PR-A"). Round-17's `localMajor = dot(d,
    // major)/majorMag3D` / `localMinor = dot(d, minorUnit)/minorRadius`
    // formula is only the correct inverse of `tessellateEllipse`'s forward
    // map when the projected `major` ⟂ `{minorX,minorY}` — for a genuine
    // tilt it silently reconstructs the WRONG angle (reviewer's repro:
    // `majorAxis={8,4,-4}`, `extrusion={0,√½,√½}` — the visible param-0
    // endpoint ITSELF resolved to a negative angle, so a cursor sitting
    // exactly on the rendered entity was misclassified as complement-side).
    //
    // Correct, projection-aware fix: solve the 2x2 linear system
    // `rel = major·cosθ + minorVec·sinθ` for `(cosθ, sinθ)` directly
    // against the REAL projected basis vectors (`major`, `{minorX,minorY}`
    // — the SAME pair the quadrant emits above already use) via Cramer's
    // rule, making NO orthogonality assumption. This is exact whenever the
    // projected basis is non-degenerate, and it collapses to round-17's
    // dot-product formula exactly whenever `major` ⟂ `{minorX,minorY}` (the
    // untilted case verified above) since Cramer's rule reduces to standard
    // orthogonal projection there — so every existing round-13..17 test
    // keeps its exact result. A near-zero determinant means the ellipse's
    // plane is viewed edge-on (its projection degenerates toward a line
    // segment) — too ill-conditioned to classify reliably, so this falls
    // back to NOT suppressing (never silently drop a legitimate extension
    // candidate; the final per-candidate aperture gate still bounds any
    // spurious result).
    const relX = cursor.x - c.x;
    const relY = cursor.y - c.y;
    const basisDet = major.x * minorY - minorX * major.y;
    let cursorOnVisibleSide = false;
    if (Math.abs(basisDet) > 1e-9) {
      const cosTheta = (relX * minorY - minorX * relY) / basisDet;
      const sinTheta = (major.x * relY - relX * major.y) / basisDet;
      const cursorParamAngle = Math.atan2(sinTheta, cosTheta);
      cursorOnVisibleSide = angleInArc(cursorParamAngle, g.start, g.end, true);
    }
    if (!cursorOnVisibleSide) {
      const extInterior = extSamples.slice(1, -1);
      let extBest: Vec2 | null = null;
      let extBestDist = Infinity;
      for (const s of extInterior) {
        const d = dist2D(s, cursor);
        if (d < extBestDist) {
          extBestDist = d;
          extBest = { x: s.x, y: s.y };
        }
      }
      if (extBest) emit("extension", extBest);
    }
  }
}

function handleSpline(
  g: Extract<RenderableEntity["geometry"], { kind: "spline" }>,
  cursor: Vec2,
  emit: Emit,
): void {
  const cps = g.controlPoints;
  if (cps.length < 1) return;
  const first = cps[0];
  const last = cps[cps.length - 1];
  if (first) emit("endpoint", toVec2(first));
  if (last) emit("endpoint", toVec2(last));
  let best: Vec2 | null = null;
  let bestDist = Infinity;
  for (let i = 0; i + 1 < cps.length; i++) {
    const a = cps[i];
    const b = cps[i + 1];
    if (!a || !b) continue;
    const foot = closestPointOnSegment(toVec2(a), toVec2(b), cursor);
    const d = dist2D(foot, cursor);
    if (d < bestDist) {
      bestDist = d;
      best = foot;
    }
  }
  if (best) emit("nearest", best);
  // OSNAP-SNAP-EXPAND — EXTENSION (bit 4096) beyond the spline's open
  // endpoints. A CLAMPED B-spline (DXF SPLINE's open/clamped knot vector —
  // first/last knot multiplicity = degree+1, the standard case) is TANGENT
  // to its control polygon at both ends (Piegl & Tiller, "The NURBS Book",
  // §3.1: "the curve is tangent to the polygon legs at the two ends"), so the
  // FIRST control-polygon leg (cps[0]→cps[1]) carries the true curve tangent
  // direction at the start, and the LAST leg (cps[n-2]→cps[n-1]) at the end —
  // exactly the LINE/POLYLINE extension test, reusing `extensionFoot`
  // against the leg instead of the rendered segment.
  //
  // @codex round-2 P2 (deliberately NOT applied — documented, not a stopgap):
  // the reviewer suggested preferring `g.tangentVectors` (DXF group 12/13
  // explicit start/end tangent) over the control-polygon leg when present.
  // Verified this would REGRESS snap↔render parity today: the live spline
  // RENDERER (`batchedLayer.ts` `geometryToLineVertices` case "spline") draws
  // `polylineToLineVertices(geometry.controlPoints, false)` — a coarse
  // control-polygon polyline that does NOT read `tangentVectors` at all
  // ("Coarse polyline through control points; PR-C upgrades to a real
  // de-Boor / Bezier sampler" — still-future). `handleSpline`'s pre-existing
  // `nearest` above ALREADY matches that same control-polygon rendering, by
  // the same one-truth-with-render principle this codebase already applies
  // to bulged LWPOLYLINE arcs (OSNAP-INTERSECT-EMIT-S2, docs/test-gaps/
  // sub-osnap.md: "shipping only the snap put the snap point OFF the chord
  // the renderer draws"). Switching EXTENSION alone to `tangentVectors` would
  // put its marker off the rendered control-polygon leg the SAME way. The
  // real fix is a documented follow-up (SPLINE-TANGENT-SNAP-EXTENSION,
  // sub-osnap.md) that must land together with PR-C's real curve tessellator
  // — not a standalone snap-only change now.
  //
  // @codex round-13 P2 (GitHub review, deliberately NOT applied — documented,
  // not a stopgap): a closed/periodic DXF SPLINE has no open end, so
  // EXTENSION should be suppressed for it, mirroring `handlePolyline`'s
  // `!g.closed` gate. `RenderableEntity["geometry"]` for `kind: "spline"`
  // has no `closed`/`periodic` field to check — verified end-to-end that
  // `closed`/`periodic` survive DXF parse into the drawing-model
  // `SplineEntity` but are dropped at the SceneGeometry construction
  // boundary (`splineGeometry.ts` / `splineFromEntity.ts`), and the spline
  // RENDERER already hardcodes every spline as open regardless
  // (`batchedLayer.ts` `polylineToLineVertices(geometry.controlPoints,
  // false)` — literal `false`). Suppressing EXTENSION here alone, without
  // also fixing the renderer, would make a closed spline LOOK open but
  // SNAP as if closed — a NEW inconsistency, worse than today's
  // consistent-but-imperfect "every spline is open" treatment. The real
  // fix (SPLINE-CLOSED-EXTENSION-SUPPRESS, sub-osnap.md) must land
  // together with the renderer fix, same discipline as the
  // OSNAP-INTERSECT-EMIT-S2 bulge/chord pair above.
  if (cps.length >= 2) {
    const startA = cps[0];
    const startB = cps[1];
    if (startA && startB) {
      const ext = extensionFoot(toVec2(startA), toVec2(startB), cursor, "before");
      if (ext) emit("extension", ext);
    }
    const endA = cps[cps.length - 2];
    const endB = cps[cps.length - 1];
    if (endA && endB) {
      const ext = extensionFoot(toVec2(endA), toVec2(endB), cursor, "after");
      if (ext) emit("extension", ext);
    }
  }
}
